/**
 * Email Service - Sends emails via Supabase Edge Function
 * Uses Resend for transactional emails
 */

import { supabaseUrl, supabaseAnonKey } from './supabase';

// Get Supabase URL for edge function calls
const SUPABASE_URL = supabaseUrl;

// Admin email for notifications
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 
  process.env.EXPO_PUBLIC_ADMIN_EMAIL || 
  'chessmapsapp@gmail.com';

/**
 * Send email via Supabase Edge Function
 * @param {Object} emailData - Email data to send
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
const sendEmail = async (emailData) => {
  const url = `${SUPABASE_URL}/functions/v1/send-email`;
  console.log(`📧 Calling edge function: ${url}`);
  console.log(`📧 Email payload type=${emailData.type}, to=${emailData.to || 'admin'}`);
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify(emailData),
    });

    console.log(`📧 Edge function response: ${response.status} ${response.statusText}`);
    const text = await response.text();
    console.log(`📧 Response body:`, text?.substring(0, 300));
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch (_) {
      // Response was not valid JSON
    }
    
    if (!response.ok || !data.success) {
      console.error('❌ Email send failed:', response.status, data.error || text?.substring(0, 200));
      return { success: false, error: data.error || `HTTP ${response.status}` };
    }

    console.log('✅ Email sent successfully:', data.messageId);
    return { success: true, messageId: data.messageId };
  } catch (error) {
    console.error('❌ Email service error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Send approval email to club owner
 * @param {string} userEmail - Club owner's email
 * @param {string} clubName - Name of the club
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendClubApprovalEmail = async (userEmail, clubName) => {
  console.log('📧 Sending club approval email to:', userEmail);
  
  return sendEmail({
    type: 'club_approval',
    to: userEmail,
    clubName,
  });
};

/**
 * Send rejection email to club owner
 * @param {string} userEmail - Club owner's email
 * @param {string} clubName - Name of the club
 * @param {string} reason - Reason for rejection
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendClubRejectionEmail = async (userEmail, clubName, reason) => {
  console.log('📧 Sending club rejection email to:', userEmail);
  
  return sendEmail({
    type: 'club_rejection',
    to: userEmail,
    clubName,
    reason,
  });
};

/**
 * Send notification email to admin about new club submission
 * @param {Object} clubData - Club submission data
 * @param {string} clubData.clubName - Name of the club
 * @param {string} clubData.email - Club owner's email
 * @param {string} clubData.pitch - Club's submission pitch
 * @param {string} clubData.address - Club's address
 * @param {Object} [clubData.socialLinks] - Optional social media links
 * @returns {Promise<{success: boolean, messageId?: string, error?: string}>}
 */
export const sendAdminNotificationEmail = async (clubData) => {
  console.log('📧 Sending admin notification for new club:', clubData.clubName);
  
  return sendEmail({
    type: 'admin_notification',
    to: ADMIN_EMAIL,
    clubName: clubData.clubName,
    clubData: {
      email: clubData.email,
      pitch: clubData.pitch,
      address: clubData.address,
      socialLinks: clubData.socialLinks,
      isClaimRequest: clubData.isClaimRequest || false,
      evidenceUrls: clubData.evidenceUrls || [],
      contactInfo: clubData.contactInfo || null,
    },
  });
};

/**
 * Send bulk approval emails
 * @param {Array<{email: string, clubName: string}>} clubs - Array of clubs to notify
 * @returns {Promise<{success: number, failed: number, errors: Array}>}
 */
export const sendBulkApprovalEmails = async (clubs) => {
  console.log('📧 Sending bulk approval emails to', clubs.length, 'clubs');
  
  const results = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const club of clubs) {
    const result = await sendClubApprovalEmail(club.email, club.clubName);
    if (result.success) {
      results.success++;
    } else {
      results.failed++;
      results.errors.push({ email: club.email, error: result.error });
    }
  }

  console.log(`📧 Bulk emails complete: ${results.success} sent, ${results.failed} failed`);
  return results;
};

/**
 * Send bulk rejection emails
 * @param {Array<{email: string, clubName: string}>} clubs - Array of clubs to notify
 * @param {string} reason - Reason for rejection
 * @returns {Promise<{success: number, failed: number, errors: Array}>}
 */
export const sendBulkRejectionEmails = async (clubs, reason) => {
  console.log('📧 Sending bulk rejection emails to', clubs.length, 'clubs');
  
  const results = {
    success: 0,
    failed: 0,
    errors: [],
  };

  for (const club of clubs) {
    const result = await sendClubRejectionEmail(club.email, club.clubName, reason);
    if (result.success) {
      results.success++;
    } else {
      results.failed++;
      results.errors.push({ email: club.email, error: result.error });
    }
  }

  console.log(`📧 Bulk emails complete: ${results.success} sent, ${results.failed} failed`);
  return results;
};

export default {
  sendClubApprovalEmail,
  sendClubRejectionEmail,
  sendAdminNotificationEmail,
  sendBulkApprovalEmails,
  sendBulkRejectionEmails,
};

